import AddIcon from '@mui/icons-material/Add'
import {
  DataGridPro,
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowsProp,
  GridToolbar
} from '@mui/x-data-grid-pro'
import { useCallback, useContext, useMemo } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetPartsMatrices } from '../../../hooks/useGetPartsMatrices/useGetPartsMatrices'
import { useGetVendorEntries } from '../../../hooks/useGetVendorEntries/useGetVendorEntries'
import { LabelsPartsInfo } from '../../../models/Labels'
import { CounterSalesContext } from '../../../providers/CounterSalesProvider/CounterSalesProvider'
import { useCreateCounterSaleNoTagsMutation } from '../../../redux/api'
import {
  useCreateCannedJobCustomPartMutation,
  useListCannedPartsQuery
} from '../../../redux/api/cannedJobApi'
import {
  CannedJobCustomPartInfo,
  CounterSaleInfoStatusEnum
} from '../../../sdk'
import { formatCurrencyInDataGrid } from '../../../utils/formatCurrencyInDataGrid'
import { getRetailPricePerUnitOfCannedPart } from '../../CannedJobs/customPartsCalculation'

const CounterSaleProductStore = () => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { activeCounterSale } = useContext(CounterSalesContext)

  const { data: products, isLoading: isLoadingListCannedParts } =
    useListCannedPartsQuery({
      authorization: accessToken,
      shop: shopId
    })
  const [createCounterSale, { isLoading: isLoadingCreateCounterSale }] =
    useCreateCounterSaleNoTagsMutation()
  const [createCustomParts, { isLoading: isLoadingCreateCustomParts }] =
    useCreateCannedJobCustomPartMutation()

  const { allPartsMatrices } = useGetPartsMatrices([])

  const addItemToCart = useCallback(
    async (info: CannedJobCustomPartInfo) => {
      if (!activeCounterSale) {
        const newSale = await createCounterSale({
          authorization: accessToken,
          counterSaleInfo: {
            shop: shopId,
            status: CounterSaleInfoStatusEnum.InProgress
          }
        })
        if ('data' in newSale) {
          createCustomParts({
            authorization: accessToken,
            cannedJobCustomPartInfo: {
              ...info,
              quantity: 1,
              cannedJob: undefined,
              shop: undefined,
              counterSale: newSale.data
            }
          })
        }
      } else {
        createCustomParts({
          authorization: accessToken,
          cannedJobCustomPartInfo: {
            ...info,
            quantity: 1,
            cannedJob: undefined,
            shop: undefined,
            counterSale: activeCounterSale.entry?.id
          }
        })
      }
    },
    [
      accessToken,
      activeCounterSale,
      createCounterSale,
      createCustomParts,
      shopId
    ]
  )

  const columns = useMemo(
    () => [
      {
        field: 'partNumber',
        headerName: LabelsPartsInfo.partNumber,
        flex: 1
      },
      { field: 'name', headerName: 'Parts ' + LabelsPartsInfo.name, flex: 1 },
      {
        field: 'retailPricePerUnit',
        headerName: LabelsPartsInfo.retailPricePerUnit,
        flex: 1,
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'vendorName',
        headerName: LabelsPartsInfo.vendorName,
        flex: 1
      },
      {
        field: 'description',
        headerName: LabelsPartsInfo.description,
        flex: 1
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Add to Cart',
        flex: 2,
        getActions: (params: GridRenderCellParams) => [
          <GridActionsCellItem
            key="Add"
            icon={<AddIcon />}
            disabled={isLoadingCreateCounterSale || isLoadingCreateCustomParts}
            label="Add"
            onClick={() => {
              const info = products?.find(
                (entry) => entry.id === params.id
              )?.info
              if (info) {
                addItemToCart(info)
              }
            }}
          />
        ]
      }
    ],
    [
      products,
      addItemToCart,
      isLoadingCreateCounterSale,
      isLoadingCreateCustomParts
    ]
  )

  const { shopVendorsAllMap } = useGetVendorEntries()

  const rows = useMemo(() => {
    const customPartRows: GridRowsProp = (products || []).map((data) => {
      const retailPricePerUnit = getRetailPricePerUnitOfCannedPart(
        data,
        allPartsMatrices
      )

      return {
        id: data.id!,
        ...data.info!,
        retailPricePerUnit,
        vendorName:
          data.info?.vendorId !== undefined
            ? shopVendorsAllMap.get(data.info?.vendorId)?.info?.name
            : data.info?.supplierName
      }
    })
    return customPartRows
  }, [products, shopVendorsAllMap, allPartsMatrices])

  return (
    <div style={{ height: 650, width: '100%' }}>
      <DataGridPro
        loading={isLoadingListCannedParts}
        slots={{
          toolbar: GridToolbar
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true
          }
        }}
        density="compact"
        rows={rows}
        columns={columns}
      />
    </div>
  )
}

export default CounterSaleProductStore
